/**
 * sfx.ts — the gameplay sound-effect API (flight/space SFX wave 2026-08-23).
 *
 * Since 2026-09-20 this file is a SHIM over the shared audio runtime
 * (lib/audio/runtime.ts): it owns no AudioContext, no cache and no graph. It
 * exists so the ~190 call sites keep their signatures while everything behind
 * them changed — the clip finds its category BUS by key (lib/audio/buses.ts),
 * passes the voice budget, may be POSITIONAL, and is loaded by the one asset
 * store (a transient failure is retried; a 404 is still permanent silence —
 * wiring may land before its audio, as ever).
 *
 * - `sfx(key, {gain, rate, cooldownMs})` — one-shot with the legacy per-KEY
 *   rate limit (a 60 Hz caller can spam; the cooldown is the first guard, the
 *   voice allocator the second).
 * - `sfxAt(key, pos, opts)` — the same, AT an absolute world point: the player
 *   hears WHERE it is. The caller's `gain` keeps owning distance (the panner
 *   gives direction only) — two attenuation curves multiplied is the bug this
 *   wave was warned about. `emitter` scopes the cooldown to one source.
 * - `sfxLoop(key, scope?)` — ONE persistent loop per (scope, key). Two systems
 *   sharing a clip no longer fight over one source; `stopSfxScope(scope)` ends
 *   one owner's loops and nobody else's.
 * - `sfxLater(ms, fn)` — a delayed sound that dies with the world it was
 *   scheduled in (thunder, the second footstep, the respawn chime).
 */
import { audioRuntime, type LoopHandle, type PlayOpts } from './audio/runtime.js';
import type { Bus } from './audio/buses.js';

export type { LoopHandle } from './audio/runtime.js';
export { inverseDistanceGain, arrivalDelayS } from './audio/spatial_math.js';

const lastAt = new Map<string, number>();

export interface SfxOpts { gain?: number; rate?: number; cooldownMs?: number }
export interface SfxAtOpts extends SfxOpts {
  /** Scope the cooldown to one source (two creatures may snarl at once; one creature may not machine-gun). */
  emitter?: string;
  /** A caption for the Sound-captions setting ("Gunfire", "Creature"): shown with its bearing. */
  caption?: string;
  bus?: Bus;
  priority?: number;
  /** Seconds to hold the sound back on the audio clock (arrival delay of a distant report). */
  delayS?: number;
  /** false = never muffled by a wall (thunder: the sky is not behind the shed). */
  occlude?: boolean;
}

function cooled(id: string, cooldownMs: number): boolean {
  const now = performance.now();
  if (now - (lastAt.get(id) ?? -1e9) < cooldownMs) return false;
  lastAt.set(id, now);
  if (lastAt.size > 512) { for (const [k, t] of lastAt) if (now - t > 10_000) lastAt.delete(k); }
  return true;
}

/** The dialogue input of the shared mix, for the versona TTS: the shared context, behind the game gate and the master.
 *  (It used to hand out the SFX master — speech now rides its OWN bus, so it can duck the music instead of fighting it.) */
export function sfxOutput(): { ctx: AudioContext; master: GainNode } | null {
  const rt = audioRuntime();
  rt.unlock();
  const master = rt.busInput('dialogue');
  return rt.ctx && master ? { ctx: rt.ctx, master } : null;
}

/** Is a key's audio installed? true = decoded, false = it will not play this session (404), null = not known yet
 *  (the call starts the load). A caller with a FALLBACK plays the fallback while null/false. */
export function sfxAvailable(key: string): boolean | null { return audioRuntime().available(key); }

/** Decode keys ahead of their first trigger (the combat HUD warms the weapons in the ring). */
export function sfxPreload(keys: readonly string[]): void { void audioRuntime().preload(keys); }

/** Fire-and-forget one-shot. Missing audio = silence. */
export function sfx(key: string, opts?: SfxOpts): void {
  if (!cooled(key, opts?.cooldownMs ?? 60)) return;
  audioRuntime().playKey(key, { gain: opts?.gain, rate: opts?.rate });
}

/** A one-shot AT an absolute world position. `gain` still carries the caller's distance curve; the panner adds direction. */
export function sfxAt(key: string, pos: { x: number; y: number; z: number }, opts?: SfxAtOpts): void {
  if (!cooled(opts?.emitter ? `${key}@${opts.emitter}` : key, opts?.cooldownMs ?? 60)) return;
  const o: PlayOpts = { gain: opts?.gain, rate: opts?.rate, pos, distance: 'caller', caption: opts?.caption, bus: opts?.bus, priority: opts?.priority, delayS: opts?.delayS, occlude: opts?.occlude };
  audioRuntime().playKey(key, o);
}

/** The persistent loop for a key, owned by `scope` (created on first ask; starts silent). */
export function sfxLoop(key: string, scope = 'legacy'): LoopHandle { return audioRuntime().loop(scope, key); }

/** Fade out ONE owner's loops (a system leaving its regime). */
export function stopSfxScope(scope: string, rampS = 0.3): void { audioRuntime().stopScope(scope, rampS); }

/** Fade out every loop in the game — a real teardown only. One system's exit wants `stopSfxScope`. */
export function stopAllSfxLoops(rampS = 0.3): void { audioRuntime().stopAllLoops(rampS); }

/** Run `fn` after `ms` — unless the world changed first. */
export function sfxLater(ms: number, fn: () => void): void { audioRuntime().later(ms, fn); }
